import { cliente } from "../http/cliente";

/*
 * Fetcher do export de extrato (Gap C — Tela 1, ação de exportar CSV).
 *
 * Camada fina sobre o `cliente` HTTP (CP-C): injeta Bearer, trata refresh/401/503
 * e normaliza erro em `ErroHttp`. NÃO trata erro — propaga a rejeição do
 * `cliente` para o hook de ação decidir.
 *
 * Contrato BE (selado): `GET /api/empresas/{empresa_id}/export?format=csv&
 * horizonte=N`. `format` é SEMPRE `csv` (V1 só suporta CSV); `horizonte` vai
 * como query param (faixa validada pelo backend). A resposta é o corpo CSV
 * (StreamingResponse), lido como BLOB — `responseType: "blob"` é OBRIGATÓRIO,
 * senão o axios tentaria parsear como JSON e corromperia o arquivo.
 *
 * `baseURL` do cliente já é `/api`, então o path aqui é relativo a ela.
 */

/** Formato do export — V1 só suporta CSV (contrato BE). */
const FORMATO_CSV = "csv";

/**
 * Extrai o `filename` do header `Content-Disposition`
 * (`attachment; filename="extrato_<id>_<data>.csv"`). Se o header estiver
 * ausente ou sem `filename`, devolve `null` — o caller aplica o fallback.
 *
 * Tolerante a aspas opcionais e a espaços ao redor do `=`.
 */
function extrairFilename(contentDisposition: unknown): string | null {
  if (typeof contentDisposition !== "string") {
    return null;
  }
  const correspondencia = /filename\s*=\s*"?([^";]+)"?/i.exec(
    contentDisposition,
  );
  const nome = correspondencia?.[1]?.trim();
  return nome !== undefined && nome.length > 0 ? nome : null;
}

/**
 * `GET /api/empresas/{empresaId}/export?format=csv&horizonte=N`.
 *
 * Devolve o `blob` do CSV e o `filename` resolvido (do `Content-Disposition`,
 * com fallback `extrato_{empresaId}_{hoje}.csv` quando o header não vem). A
 * janela é RETROATIVA no backend: os últimos `horizonte` dias.
 */
export async function exportarExtrato(
  empresaId: string,
  horizonte: number,
): Promise<{ blob: Blob; filename: string }> {
  const resposta = await cliente.get<Blob>(
    `/empresas/${empresaId}/export`,
    {
      params: { format: FORMATO_CSV, horizonte },
      responseType: "blob",
    },
  );

  const hoje = new Date().toISOString().slice(0, 10);
  const fallback = `extrato_${empresaId}_${hoje}.csv`;
  const filename =
    extrairFilename(resposta.headers["content-disposition"]) ?? fallback;

  return { blob: resposta.data, filename };
}
